import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { useApp } from "../store";
import { normalizeMarkdownInput, parseMarkdown } from "../lib/parseMarkdown";
import { importSettingsFromFile } from "../lib/persistence";
export default function UploadScreen() {
    const { settings, setSettings, loadDocument } = useApp();
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const dropRef = useRef(null);
    async function handleFile(file) {
        setError(null);
        setBusy(true);
        try {
            const text = await file.text();
            // Markdown einmalig normalisieren — danach ist `normalized` die einzige
            // Source-of-Truth. Mdast-Offsets im DOM und Editor-Inhalt beziehen sich
            // beide auf denselben Text, damit Click-to-Edit-Inserts genau treffen.
            const { normalized, suggestedMeta } = normalizeMarkdownInput(text);
            const { html, headings, images, wordCount } = await parseMarkdown(normalized, {
                smartQuotes: settings.smartQuotes,
                language: settings.meta.language,
                skipNormalize: true,
            });
            const meta = { ...settings.meta };
            if (suggestedMeta.title && settings.meta.title === "Mein Buch")
                meta.title = suggestedMeta.title;
            if (suggestedMeta.subtitle && !settings.meta.subtitle)
                meta.subtitle = suggestedMeta.subtitle;
            if (suggestedMeta.author && settings.meta.author === "Unbekannt")
                meta.author = suggestedMeta.author;
            setSettings({ ...settings, meta });
            loadDocument({
                rawMarkdown: normalized,
                bodyHtml: html,
                headings,
                images,
                wordCount,
                filename: file.name,
            });
        }
        catch (e) {
            setError(String(e.message || e));
        }
        finally {
            setBusy(false);
        }
    }
    async function loadSampleBook() {
        setError(null);
        setBusy(true);
        try {
            const res = await fetch("sample/beispielbuch.md");
            if (!res.ok)
                throw new Error("Beispieldatei nicht gefunden (" + res.status + ")");
            const text = await res.text();
            const blob = new File([text], "beispielbuch.md", { type: "text/markdown" });
            await handleFile(blob);
        }
        catch (e) {
            setError(String(e.message || e));
        }
        finally {
            setBusy(false);
        }
    }
    async function handleJsonImport(file) {
        try {
            const s = await importSettingsFromFile(file);
            setSettings(s);
            setError(null);
        }
        catch (e) {
            setError("JSON konnte nicht geladen werden: " + String(e.message));
        }
    }
    function onDragOver(e) {
        e.preventDefault();
        dropRef.current?.classList.add("dropzone-hover");
    }
    function onDragLeave() {
        dropRef.current?.classList.remove("dropzone-hover");
    }
    function onDrop(e) {
        e.preventDefault();
        dropRef.current?.classList.remove("dropzone-hover");
        const file = e.dataTransfer.files?.[0];
        if (!file)
            return;
        if (/\.(md|markdown|txt)$/i.test(file.name))
            handleFile(file);
        else if (/\.json$/i.test(file.name))
            handleJsonImport(file);
        else
            setError("Bitte .md oder .json-Datei wählen.");
    }
    return (_jsxs("main", { className: "upload-wrap", children: [_jsxs("header", { className: "upload-hero", children: [_jsx("h1", { children: "Aus Markdown wird ein druckfertiges Buch." }), _jsx("p", { children: "Type.ai, Sudowrite, Obsidian \u2014 egal woher." })] }), _jsxs("section", { className: "frame", children: [_jsxs("div", { className: "frame-head", children: [_jsx("span", { className: "title", children: "Schritt 1" }), _jsx("span", { className: "crumb", children: "\u203A MD-Datei laden" }), _jsx("span", { className: "grow" }), _jsx("span", { className: "label", children: "Drag & Drop" })] }), _jsxs("div", { style: { padding: 20 }, children: [_jsxs("div", { ref: dropRef, className: "dropzone", onDragOver: onDragOver, onDragLeave: onDragLeave, onDrop: onDrop, children: [_jsx("div", { className: "dropzone-icon", children: "\u2913" }), _jsxs("div", { children: [_jsx("b", { children: "Markdown hier ablegen" }), " oder", " ", _jsxs("label", { className: "btn", children: ["Datei w\u00E4hlen", _jsx("input", { type: "file", accept: ".md,.markdown,.txt", hidden: true, onChange: (e) => e.target.files?.[0] && handleFile(e.target.files[0]) })] })] }), _jsx("div", { className: "file-types", children: "unterst\u00FCtzt: .md, .markdown \u00B7 max. 20 MB" }), busy && _jsx("div", { style: { marginTop: 12, color: "var(--ink-3)" }, children: "Lade & parse\u2026" }), error && _jsxs("div", { style: { marginTop: 12, color: "#b03a2e" }, children: ["Fehler: ", error] })] }), _jsx("div", { className: "upload-alt", children: "\u2014 oder \u2014" }), _jsxs("div", { className: "upload-import", children: [_jsx("span", { className: "label", children: "JSON-Vorlage importieren" }), _jsx("span", { style: { color: "var(--ink-3)", fontSize: 12 }, children: "Settings einer vorherigen Generierung laden" }), _jsxs("label", { className: "btn ghost", children: ["Vorlage w\u00E4hlen", _jsx("input", { type: "file", accept: ".json,application/json", hidden: true, onChange: (e) => e.target.files?.[0] && handleJsonImport(e.target.files[0]) })] })] })] })] }), _jsx("div", { style: { marginTop: 20, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }, children: _jsx("button", { className: "btn ghost", onClick: loadSampleBook, disabled: busy, children: "Beispielbuch laden (\u201EEine kleine Reise\")" }) }), _jsxs("div", { style: { marginTop: 10, color: "var(--ink-3)", fontSize: 12, textAlign: "center", maxWidth: 540, marginLeft: "auto", marginRight: "auto" }, children: ["Das Beispielbuch referenziert zwei Test-Bilder, die im Editor zum Hochladen bereitstehen. Direkter Download zum Mitmachen:", " ", _jsx("a", { href: "sample/bilder/berg-sonnenaufgang.jpg", download: true, style: { color: "var(--accent)" }, children: "berg-sonnenaufgang.jpg" }), " · ", _jsx("a", { href: "sample/bilder/aufgeschlagenes-buch.jpg", download: true, style: { color: "var(--accent)" }, children: "aufgeschlagenes-buch.jpg" })] }), _jsx("div", { style: { marginTop: 14, color: "var(--ink-3)", fontSize: 12, textAlign: "center" }, children: "Dein Buch verl\u00E4sst nie deinen Browser. md2bookpdf ist eine reine Client-App." })] }));
}
